import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import MuiTooltip from '@material-ui/core/Tooltip';
import Paper from '@material-ui/core/Paper';

import { Arrow } from 'react-popper';

import pxToRem from 'styles/pxToRem';

const styles = theme => ({
  root: {
    display: 'inline-block',
    flexDirection: 'inherit',
  },
  popper: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    margin: 15,
    zIndex: 2500,
    '&[data-placement="top"]': {
      '& $tooltip': {
        transformOrigin: 'center bottom',
      },
      '& $arrow': {
        width: 30,
        height: 20,
        bottom: -20,
      },
      '& $arrowIcon': {
        left: '50%',
        bottom: 12,
        marginLeft: -8,
      },
    },
    '&[data-placement="right"]': {
      '& $tooltip': {
        transformOrigin: 'left center',
      },
      '& $arrow': {
        width: 20,
        height: 30,
        top: '50%',
        left: -20,
      },
      '& $arrowIcon': {
        top: '50%',
        left: 12,
        marginTop: -8,
      },
      '&$popperDisableGutters $paperDark $arrow': {
        marginTop: -3,
      },
    },
    '&[data-placement="bottom"]': {
      '& $tooltip': {
        transformOrigin: 'center top',
      },
      '& $arrow': {
        width: 30,
        height: 20,
        top: -20,
      },
      '& $arrowIcon': {
        left: '50%',
        top: 12,
        marginLeft: -8,
      },
    },
    '&[data-placement="left"]': {
      '& $tooltip': {
        transformOrigin: 'right center',
      },
      '& $arrow': {
        width: 20,
        height: 30,
        top: '50%',
        right: -20,
      },
      '& $arrowIcon': {
        top: '50%',
        right: 8,
        marginTop: -8,
      },
    },
    '&[data-placement="left-end"]': {
      '& $tooltip': {
        transformOrigin: 'right center',
      },
      '& $arrow': {
        width: 20,
        height: 30,
        top: '50%',
        right: -20,
      },
      '& $arrowIcon': {
        top: '50%',
        right: 8,
        marginTop: -12,
      },
    },
  },
  popperDisableGutters: {
    margin: 0,
    '& $paperWhite': {
      padding: '5px 10px',
    },
  },
  tooltip: {
    padding: 0,
    margin: 0,
    background: 'transparent',
  },
  tooltipDisablePointerEvents: {
    pointerEvents: 'none',
  },
  paper: {
    padding: 20,
    minWidth: 130,
    textAlign: 'center',
    borderRadius: 4,
  },
  paperAlignLeft: {
    textAlign: 'left',
  },
  paperAlignRight: {
    textAlign: 'right',
  },
  paperAlignCenter: {
    textAlign: 'center',
  },
  paperWhite: {
    background: theme.palette.common.white,
  },
  paperGrey: {
    backgroundColor: theme.palette.grey[100],
  },
  paperDark: {
    padding: '5px 10px',
    minWidth: 0,
    backgroundColor: theme.palette.secondary[500],
    borderRadius: 2,
    fontSize: pxToRem(12),
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  arrow: {
    position: 'absolute',
    overflow: 'hidden',
    zIndex: '1',
  },
  arrowIcon: {
    position: 'relative',
    width: 16,
    height: 16,
    transform: 'rotate(45deg)',
    borderRadius: '4px 0 0 0',
    boxShadow: theme.shadows[2],
  },
  arrowIconWhite: {
    background: theme.palette.common.white,
  },
  arrowIconGrey: {
    background: theme.palette.grey[100],
  },
  arrowIconDark: {
    backgroundColor: theme.palette.secondary[500],
  },
});

const Tooltip = ({
  id,
  open,
  content,
  placement,
  color,
  classes,
  className,
  children,
  align,
  disableGutters,
  disableFocusListener,
  disableHoverListener,
  disableTouchListener,
  disablePointerEvents,
  disabled,
  onMouseEnter,
  onMouseLeave,
  onOpen,
  onClose,
  popperClassName,
}) => {
  if (disabled || !content) {
    return (
      <div className={classNames(classes.root, className)}>
        {children}
      </div>
    );
  }

  const title = (
    <Paper
      className={classNames(classes.paper, {
        [classes.paperWhite]: color === 'white',
        [classes.paperGrey]: color === 'grey',
        [classes.paperDark]: color === 'dark',
        [classes.paperAlignLeft]: align === 'left',
        [classes.paperAlignRight]: align === 'right',
        [classes.paperAlignCenter]: align === 'center',
      })}
      elevation={2}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Arrow className={classes.arrow}>
        <div
          className={classNames(classes.arrowIcon, {
            [classes.arrowIconWhite]: color === 'white',
            [classes.arrowIconGrey]: color === 'grey',
            [classes.arrowIconDark]: color === 'dark',
          })}
        />
      </Arrow>
      {content}
    </Paper>
  );

  return (
    <MuiTooltip
      {... id ? { id } : {}}
      {... open !== null ? { open } : {}}
      {... disableFocusListener ? { disableFocusListener } : {}}
      {... disableHoverListener ? { disableHoverListener } : {}}
      {... disableTouchListener ? { disableTouchListener } : {}}
      title={title}
      placement={placement}
      classes={{
        popper: classNames(classes.popper, popperClassName, {
          [classes.popperDisableGutters]: disableGutters,
        }),
        tooltip: classNames(classes.tooltip, {
          [classes.tooltipDisablePointerEvents]: disablePointerEvents,
        }),
      }}
      onOpen={onOpen}
      onClose={onClose}
    >
      <div className={classNames(classes.root, className)}>
        {children}
      </div>
    </MuiTooltip>
  );
};

Tooltip.propTypes = {
  popperClassName: PropTypes.string,
  id: PropTypes.string,
  open: PropTypes.bool,
  content: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  color: PropTypes.oneOf(['white', 'grey', 'dark']),
  className: PropTypes.string,
  disableGutters: PropTypes.bool,
  disableFocusListener: PropTypes.bool,
  disableHoverListener: PropTypes.bool,
  disableTouchListener: PropTypes.bool,
  disablePointerEvents: PropTypes.bool,
  disabled: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    popper: PropTypes.string.isRequired,
    popperDisableGutters: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    tooltipDisablePointerEvents: PropTypes.string.isRequired,
    paper: PropTypes.string.isRequired,
    paperWhite: PropTypes.string.isRequired,
    paperGrey: PropTypes.string.isRequired,
    paperDark: PropTypes.string.isRequired,
    arrow: PropTypes.string.isRequired,
    arrowIcon: PropTypes.string.isRequired,
    arrowIconWhite: PropTypes.string.isRequired,
    arrowIconGrey: PropTypes.string.isRequired,
    arrowIconDark: PropTypes.string.isRequired,
  }).isRequired,
};

Tooltip.defaultProps = {
  id: null,
  open: null,
  placement: 'right',
  color: 'white',
  className: '',
  disableGutters: false,
  disableFocusListener: false,
  disableHoverListener: false,
  disableTouchListener: false,
  align: 'center',
  disabled: false,
  disablePointerEvents: true,
  popperClassName: '',
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  onOpen: () => {},
  onClose: () => {},
};

export default withStyles(styles)(Tooltip);



// WEBPACK FOOTER //
// ./src/components/common/Tooltip.js